import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { logout } from '@/services/auth';
import { tileService } from '@/services/tiles';
import { fsiV2Service, QuickSummaryResponse } from '@/services/fsiV2';
import {
    LogOut,
    Search,
    Layers,
    ChevronDown,
    Box,
    Map as MapIcon,
    MessageSquare,
    X,
    ChevronLeft,
    Info
} from 'lucide-react';
import ChatPanel from '@/components/v2/ChatPanel';
import PlotSummaryCard from '@/components/v2/PlotSummaryCard';
import 'mapbox-gl/dist/mapbox-gl.css';

// If token exists in env
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
if (!MAPBOX_TOKEN) {
    console.error('Mapbox Access Token is missing! Check your .env file.');
}
mapboxgl.accessToken = MAPBOX_TOKEN || '';

// Survey zone color mapping
const getSurveyZoneColorExpression = (): any => {
    return [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        '#FFD700',  // Gold when selected

        // Residential Zones (R1, R2, R3, R4, R5) - Yellow/Orange tones
        ['in', ['upcase', ['coalesce', ['get', 'zone'], '']],
            ['literal', ['R', 'R1', 'R2', 'R3', 'R4', 'R5', 'RESIDENTIAL ZONE']]],
        '#FFE4B5',  // Moccasin

        // Agricultural Zones - Light Green
        ['in', ['upcase', ['coalesce', ['get', 'zone'], '']],
            ['literal', ['AG', 'AG-1', 'AG1', 'NON AG']]],
        '#C8E6C9',

        // Gamtal & Village - Beige
        ['in', ['upcase', ['coalesce', ['get', 'zone'], '']],
            ['literal', ['GM', 'GME', 'GAMTAL', 'VILLAGE']]],
        '#F5DEB3',

        // Commercial - Red
        ['in', ['upcase', ['coalesce', ['get', 'zone'], '']],
            ['literal', ['C3', 'C', 'C1', 'C2', 'COMMERCIAL']]],
        '#FF6B6B',

        // Industrial - Purple
        ['in', ['upcase', ['coalesce', ['get', 'zone'], '']],
            ['literal', ['I', 'I3', 'IG', 'INDUSTRIAL']]],
        '#9B59B6',

        // Default - Light Gray
        '#E0E0E0'
    ];
};

// Land use color mapping for FPS plots
const getLandUseColorExpression = () => {
    return [
        'case',
        // Educational - Blue
        ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']],
            ['literal', ['education', 'school', 'library', 'college']]],
        '#3498DB',

        // Healthcare - Green
        ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']],
            ['literal', ['health', 'medical', 'hospital']]],
        '#2ECC71',

        // Commercial - Red
        ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']],
            ['literal', ['commercial', 'market', 'shopping']]],
        '#FF6B6B',

        // Industrial - Purple
        ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']],
            ['literal', ['industrial', 'factory']]],
        '#9B59B6',

        // Recreation - Dark Green
        ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']],
            ['literal', ['recreation', 'park', 'garden']]],
        '#27AE60',

        // Residential - Moccasin
        ['in', ['downcase', ['coalesce', ['get', 'reser_use'], '']],
            ['literal', ['housing', 'residential']]],
        '#FFE4B5',

        // Default - Light green
        '#E8F5E8'
    ];
};

const MapV2Page: React.FC = () => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const selectedFeatureRef = useRef<{ id: string | number; source: string; sourceLayer: string } | null>(null);

    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [is3DView, setIs3DView] = useState(false);
    const [showLayers, setShowLayers] = useState(false);
    const [layerVisibility, setLayerVisibility] = useState({
        fpsPlots: true,
        surveyPlots: true,
        roads: true,
        buildings3D: true
    });

    // Search
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Selected plot state
    const [selectedGid, setSelectedGid] = useState<number | null>(null);
    const [selectedPlotCenter, setSelectedPlotCenter] = useState<{ lng: number; lat: number } | null>(null);
    const [plotSummary, setPlotSummary] = useState<QuickSummaryResponse | null>(null);
    const [isLoadingSummary, setIsLoadingSummary] = useState(false);

    // Chat panel state
    const [isChatOpen, setIsChatOpen] = useState(false);

    // Tower visualization state
    const [showingTowers, setShowingTowers] = useState(false);
    const towerMarkersRef = useRef<mapboxgl.Marker[]>([]);

    // Map style state
    const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
    const [showStyleSelector, setShowStyleSelector] = useState(false);

    // Handle tower visualization on map
    const handleVisualizeTowers = (scenario: any) => {
        if (!map.current || !selectedGid || !selectedPlotCenter) return;

        // Clear existing markers
        towerMarkersRef.current.forEach(marker => marker.remove());
        towerMarkersRef.current = [];

        const towerCount = scenario.tower_count || 1;
        const floors = scenario.tower_configuration?.floors_per_tower || 7;

        // Use the selected plot center for accurate positioning
        const center = selectedPlotCenter;

        // Calculate tower positions based on count
        const positions: [number, number][] = [];
        const offset = 0.0002; // Small offset for tower spacing

        if (towerCount === 1) {
            positions.push([center.lng, center.lat]);
        } else if (towerCount === 2) {
            positions.push([center.lng - offset, center.lat]);
            positions.push([center.lng + offset, center.lat]);
        } else if (towerCount === 3) {
            positions.push([center.lng - offset, center.lat]);
            positions.push([center.lng, center.lat + offset * 0.8]);
            positions.push([center.lng + offset, center.lat]);
        } else if (towerCount === 4) {
            positions.push([center.lng - offset, center.lat + offset * 0.5]);
            positions.push([center.lng + offset, center.lat + offset * 0.5]);
            positions.push([center.lng - offset, center.lat - offset * 0.5]);
            positions.push([center.lng + offset, center.lat - offset * 0.5]);
        }

        // Add markers for each tower
        positions.forEach((pos, index) => {
            // Create custom marker element
            const el = document.createElement('div');
            el.className = 'tower-marker';
            el.innerHTML = `
                <div style="
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    animation: bounceIn 0.5s ease ${index * 0.1}s both;
                ">
                    <div style="
                        background: linear-gradient(135deg, #3B82F6, #1D4ED8);
                        color: white;
                        width: ${Math.max(24, 32 - towerCount * 2)}px;
                        height: ${Math.min(60, floors * 4)}px;
                        border-radius: 4px 4px 0 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: bold;
                        font-size: 10px;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                        transform-origin: bottom center;
                    ">
                        T${index + 1}
                    </div>
                    <div style="
                        background: #1E40AF;
                        width: ${Math.max(28, 36 - towerCount * 2)}px;
                        height: 6px;
                        border-radius: 0 0 2px 2px;
                    "></div>
                    <div style="
                        font-size: 8px;
                        color: #1E40AF;
                        font-weight: 500;
                        margin-top: 2px;
                        background: white;
                        padding: 1px 4px;
                        border-radius: 2px;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                    ">G+${floors - 1}</div>
                </div>
            `;

            const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
                .setLngLat(pos)
                .addTo(map.current!);

            towerMarkersRef.current.push(marker);
        });

        // Add CSS animation if not exists
        if (!document.getElementById('tower-marker-styles')) {
            const style = document.createElement('style');
            style.id = 'tower-marker-styles';
            style.textContent = `
                @keyframes bounceIn {
                    0% { transform: scale(0) translateY(20px); opacity: 0; }
                    60% { transform: scale(1.1) translateY(-5px); opacity: 1; }
                    100% { transform: scale(1) translateY(0); opacity: 1; }
                }
            `;
            document.head.appendChild(style);
        }

        setShowingTowers(true);

        // Enable 3D view for better visualization
        if (!is3DView) {
            toggle3DView();
        }
    };

    // Clear towers when plot changes
    useEffect(() => {
        if (!selectedGid) {
            towerMarkersRef.current.forEach(marker => marker.remove());
            towerMarkersRef.current = [];
            setShowingTowers(false);
        }
    }, [selectedGid]);

    // Initialize map
    useEffect(() => {
        if (!mapContainer.current || map.current) return;

        if (!mapboxgl.accessToken) {
            alert('Mapbox Access Token is missing. Please check your .env file.');
            return;
        }

        // Add custom CSS for map
        const style = document.createElement('style');
        style.textContent = `
      .mapboxgl-popup.v2-popup .mapboxgl-popup-content {
        padding: 0;
        border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
        border: none;
        overflow: hidden;
      }
      .mapboxgl-popup.v2-popup .mapboxgl-popup-tip {
        border-top-color: white;
      }
      .mapboxgl-popup.v2-popup .mapboxgl-popup-close-button {
        display: none;
      }
    `;
        document.head.appendChild(style);

        try {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyle,
                center: [72.5797, 23.0225], // Ahmedabad
                zoom: 13,
                pitch: 0,
                bearing: 0,
                attributionControl: true,
                preserveDrawingBuffer: true
            });

            map.current.on('error', (e) => {
                console.error('Mapbox error:', e);
            });
        } catch (error) {
            console.error('Failed to initialize Mapbox:', error);
            alert('Failed to initialize map. Check Mapbox token.');
            return;
        }

        map.current.addControl(new mapboxgl.NavigationControl({
            visualizePitch: true,
            showCompass: true
        }), 'top-right');

        map.current.on('load', () => {
            setIsMapLoaded(true);
            setTimeout(() => addVectorTileLayers(), 100);
        });

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, []);

    // Add vector tile layers
    const addVectorTileLayers = () => {
        if (!map.current) return;

        try {
            const vectorSource = tileService.getVectorTileSource();
            map.current.addSource('kshetram-tiles', vectorSource);

            // Survey plots fill
            map.current.addLayer({
                id: 'survey-fill',
                type: 'fill',
                source: 'kshetram-tiles',
                'source-layer': 'road_network',
                filter: ['==', '_layer_type', 'dp_survey'],
                paint: {
                    'fill-color': getSurveyZoneColorExpression(),
                    'fill-outline-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false],
                        '#FFD700',
                        '#27AE60'
                    ],
                    'fill-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0.4, 16, 0.6]
                },
                minzoom: 12,
                maxzoom: 18
            });

            // Survey plots outline
            map.current.addLayer({
                id: 'survey-line',
                type: 'line',
                source: 'kshetram-tiles',
                'source-layer': 'road_network',
                filter: ['==', '_layer_type', 'dp_survey'],
                paint: {
                    'line-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false],
                        '#FFD700',
                        '#27AE60'
                    ],
                    'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1.5, 16, 2.5],
                    'line-opacity': 1
                },
                minzoom: 12,
                maxzoom: 18
            });

            // FPS plots fill
            map.current.addLayer({
                id: 'fps-fill',
                type: 'fill',
                source: 'kshetram-tiles',
                'source-layer': 'road_network',
                filter: ['==', '_layer_type', 'fps_plot'],
                paint: {
                    'fill-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false],
                        '#FFD700',
                        getLandUseColorExpression()
                    ],
                    'fill-outline-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false],
                        '#FFD700',
                        '#1E88E5'
                    ],
                    'fill-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 0.7]
                },
                minzoom: 10,
                maxzoom: 18
            });

            // FPS plots outline
            map.current.addLayer({
                id: 'fps-line',
                type: 'line',
                source: 'kshetram-tiles',
                'source-layer': 'road_network',
                filter: ['==', '_layer_type', 'fps_plot'],
                paint: {
                    'line-color': [
                        'case',
                        ['boolean', ['feature-state', 'selected'], false],
                        '#FFD700',
                        '#1E88E5'
                    ],
                    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 14, 3],
                    'line-opacity': 1
                },
                minzoom: 10,
                maxzoom: 18
            });

            // Roads layer
            map.current.addLayer({
                id: 'roads-line',
                type: 'line',
                source: 'kshetram-tiles',
                'source-layer': 'road_network',
                filter: ['==', '_layer_type', 'road_segment'],
                paint: {
                    'line-color': '#E74C3C',
                    'line-width': ['interpolate', ['linear'], ['zoom'], 12, 2, 16, 4],
                    'line-opacity': 0.7
                },
                minzoom: 10,
                maxzoom: 18
            });

            // Click handler for plot selection
            map.current.on('click', 'fps-fill', handlePlotClick);
            map.current.on('click', 'survey-fill', handlePlotClick);

            // Cursor change on hover
            map.current.on('mouseenter', 'fps-fill', () => {
                if (map.current) map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'fps-fill', () => {
                if (map.current) map.current.getCanvas().style.cursor = '';
            });
            map.current.on('mouseenter', 'survey-fill', () => {
                if (map.current) map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'survey-fill', () => {
                if (map.current) map.current.getCanvas().style.cursor = '';
            });

        } catch (error) {
            console.error('Error adding vector tile layers:', error);
        }
    };

    // Handle plot click
    const handlePlotClick = async (e: mapboxgl.MapMouseEvent) => {
        if (!map.current || !e.features || e.features.length === 0) return;

        const feature = e.features[0];
        const gid = feature.properties?.gid || feature.id;

        if (!gid) return;

        // Clear previous selection
        if (selectedFeatureRef.current) {
            map.current.setFeatureState(
                {
                    source: selectedFeatureRef.current.source,
                    sourceLayer: selectedFeatureRef.current.sourceLayer,
                    id: selectedFeatureRef.current.id
                },
                { selected: false }
            );
        }

        // Set new selection
        const layerType = feature.properties?._layer_type;
        const sourceLayer = 'road_network';
        const featureId = feature.id || gid;

        map.current.setFeatureState(
            { source: 'kshetram-tiles', sourceLayer, id: featureId },
            { selected: true }
        );

        selectedFeatureRef.current = {
            id: featureId,
            source: 'kshetram-tiles',
            sourceLayer
        };

        // Update state and fetch summary
        setSelectedGid(Number(gid));
        setSelectedPlotCenter({ lng: e.lngLat.lng, lat: e.lngLat.lat }); // Save plot center for tower visualization
        setIsChatOpen(false);

        // Fetch quick summary
        setIsLoadingSummary(true);
        setPlotSummary(null);

        try {
            const summary = await fsiV2Service.getQuickSummary(Number(gid));
            setPlotSummary(summary);
        } catch (error) {
            console.error('Error fetching plot summary:', error);
            // Create a minimal summary from feature properties
            setPlotSummary({
                gid: Number(gid),
                fp_no: feature.properties?.fp_no || String(gid),
                fps_code: feature.properties?.fps_code || String(gid),
                area_sqm: feature.properties?.area_sqm || 0,
                zone_code: feature.properties?.zone || 'Unknown',
                base_fsi: feature.properties?.base_fsi || 1.2,
                max_fsi: feature.properties?.max_fsi || 1.8,
                estimated_buildable_sqm: feature.properties?.area_sqm ? feature.properties.area_sqm * 1.2 : 0,
                location: feature.properties?.village || 'Gujarat',
                tps: feature.properties?.tps_no || 'N/A',
                road_width_m: feature.properties?.road_width || 12,
                premium_applicable: false
            });
        } finally {
            setIsLoadingSummary(false);
        }
    };

    // Toggle 3D view
    const toggle3DView = () => {
        if (!map.current) return;

        if (is3DView) {
            map.current.easeTo({ pitch: 0, bearing: 0, duration: 1000 });

            // Remove 3D buildings layer if exists
            if (map.current.getLayer('3d-buildings')) {
                map.current.removeLayer('3d-buildings');
            }
        } else {
            map.current.easeTo({ pitch: 60, bearing: -17.6, duration: 1000 });

            // Add 3D buildings layer
            if (!map.current.getLayer('3d-buildings')) {
                const layers = map.current.getStyle().layers || [];
                const labelLayerId = layers.find(
                    (layer) => layer.type === 'symbol' && layer.layout?.['text-field']
                )?.id;

                map.current.addLayer(
                    {
                        id: '3d-buildings',
                        source: 'composite',
                        'source-layer': 'building',
                        filter: ['==', 'extrude', 'true'],
                        type: 'fill-extrusion',
                        minzoom: 15,
                        paint: {
                            'fill-extrusion-color': '#aaa',
                            'fill-extrusion-height': ['get', 'height'],
                            'fill-extrusion-base': ['get', 'min_height'],
                            'fill-extrusion-opacity': 0.6
                        }
                    },
                    labelLayerId
                );
            }
        }

        setIs3DView(!is3DView);
    };

    // Toggle layer visibility
    const toggleLayer = (layerName: keyof typeof layerVisibility) => {
        if (!map.current) return;

        const layerMapping: Record<string, string[]> = {
            fpsPlots: ['fps-fill', 'fps-line'],
            surveyPlots: ['survey-fill', 'survey-line'],
            roads: ['roads-line'],
            buildings3D: ['3d-buildings']
        };

        const layers = layerMapping[layerName];
        const newVisibility = !layerVisibility[layerName];

        layers.forEach(layer => {
            if (map.current?.getLayer(layer)) {
                map.current.setLayoutProperty(
                    layer,
                    'visibility',
                    newVisibility ? 'visible' : 'none'
                );
            }
        });

        setLayerVisibility(prev => ({ ...prev, [layerName]: newVisibility }));
    };

    // Handle search
    const handleSearch = async () => {
        if (!searchQuery.trim() || !map.current) return;

        setIsSearching(true);

        try {
            // Try to parse as GID
            const gidMatch = searchQuery.match(/^\d+$/);
            if (gidMatch) {
                const gid = parseInt(gidMatch[0]);
                const summary = await fsiV2Service.getQuickSummary(gid);

                if (summary) {
                    setSelectedGid(gid);
                    setPlotSummary(summary);
                    // Center map on plot location if available
                }
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    };

    // Handle logout
    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    // Change map style
    const changeMapStyle = (newStyle: string) => {
        if (!map.current) return;

        const center = map.current.getCenter();
        const zoom = map.current.getZoom();
        const bearing = map.current.getBearing();
        const pitch = map.current.getPitch();

        setMapStyle(newStyle);
        map.current.setStyle(newStyle);

        map.current.once('style.load', () => {
            if (!map.current) return;

            map.current.setCenter(center);
            map.current.setZoom(zoom);
            map.current.setBearing(bearing);
            map.current.setPitch(pitch);

            addVectorTileLayers();
        });
    };

    return (
        <div className="h-screen w-screen flex bg-gray-100 overflow-hidden">
            {/* Main Map Area */}
            <div className="flex-1 relative">
                {/* Map Container */}
                <div ref={mapContainer} className="w-full h-full" />

                {/* Header */}
                <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between pointer-events-none z-10">
                    {/* Left side - branding and search */}
                    <div className="flex items-center gap-4 pointer-events-auto">
                        {/* Logo */}
                        <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                                <MapIcon className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-bold text-gray-800">Kshetram</span>
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">V2</span>
                        </div>

                        {/* Search */}
                        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg flex items-center overflow-hidden">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="Search by GID or location..."
                                className="px-4 py-2.5 w-64 text-sm border-0 focus:ring-0 bg-transparent"
                            />
                            <button
                                onClick={handleSearch}
                                disabled={isSearching}
                                className="px-4 py-2.5 bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                            >
                                <Search className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Right side - controls */}
                    <div className="flex items-center gap-2 pointer-events-auto">
                        {/* Map Style */}
                        <div className="relative">
                            <button
                                onClick={() => setShowStyleSelector(!showStyleSelector)}
                                className="bg-white/95 backdrop-blur-sm p-2.5 rounded-xl shadow-lg hover:bg-white transition-colors flex items-center gap-2"
                            >
                                <MapIcon className="w-4 h-4 text-gray-600" />
                                <ChevronDown className="w-3 h-3 text-gray-400" />
                            </button>

                            {showStyleSelector && (
                                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-2 min-w-40">
                                    {[
                                        { id: 'mapbox://styles/mapbox/streets-v12', label: 'Streets' },
                                        { id: 'mapbox://styles/mapbox/satellite-streets-v12', label: 'Satellite' },
                                        { id: 'mapbox://styles/mapbox/light-v11', label: 'Light' },
                                        { id: 'mapbox://styles/mapbox/dark-v11', label: 'Dark' }
                                    ].map(style => (
                                        <button
                                            key={style.id}
                                            onClick={() => {
                                                changeMapStyle(style.id);
                                                setShowStyleSelector(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${mapStyle === style.id ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-50'
                                                }`}
                                        >
                                            {style.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Layers */}
                        <div className="relative">
                            <button
                                onClick={() => setShowLayers(!showLayers)}
                                className="bg-white/95 backdrop-blur-sm p-2.5 rounded-xl shadow-lg hover:bg-white transition-colors"
                            >
                                <Layers className="w-4 h-4 text-gray-600" />
                            </button>

                            {showLayers && (
                                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-3 min-w-48">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Layers</h4>
                                    {Object.entries(layerVisibility).map(([key, visible]) => (
                                        <label key={key} className="flex items-center gap-2 py-1.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={visible}
                                                onChange={() => toggleLayer(key as keyof typeof layerVisibility)}
                                                className="rounded text-emerald-500 focus:ring-emerald-500"
                                            />
                                            <span className="text-sm text-gray-700">
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 3D Toggle */}
                        <button
                            onClick={toggle3DView}
                            className={`bg-white/95 backdrop-blur-sm p-2.5 rounded-xl shadow-lg transition-colors ${is3DView ? 'bg-emerald-500 text-white' : 'hover:bg-white text-gray-600'
                                }`}
                        >
                            <Box className="w-4 h-4" />
                        </button>

                        {/* Logout */}
                        <button
                            onClick={handleLogout}
                            className="bg-white/95 backdrop-blur-sm p-2.5 rounded-xl shadow-lg hover:bg-red-50 text-gray-600 hover:text-red-500 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Plot Summary Card - Bottom left */}
                {selectedGid && (
                    <div className="absolute bottom-6 left-6 w-80 z-10">
                        <PlotSummaryCard
                            plot={plotSummary || {
                                gid: selectedGid,
                                fp_no: String(selectedGid),
                                fps_code: String(selectedGid),
                                area_sqm: 0,
                                zone_code: 'Loading...',
                                base_fsi: 0,
                                max_fsi: 0,
                                estimated_buildable_sqm: 0,
                                location: 'Loading...',
                                tps: '',
                                road_width_m: 0,
                                premium_applicable: false
                            }}
                            loading={isLoadingSummary}
                            onStartChat={() => setIsChatOpen(true)}
                        />
                    </div>
                )}

                {/* Floating Chat Toggle Button */}
                {selectedGid && !isChatOpen && (
                    <div className="absolute bottom-6 right-6 z-10">
                        <button
                            onClick={() => setIsChatOpen(true)}
                            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                        >
                            <MessageSquare className="w-6 h-6" />
                            <span className="font-medium">Analyze Plot</span>
                        </button>
                    </div>
                )}

                {/* V2 Badge */}
                <div className="absolute bottom-6 right-6 z-0 flex items-center gap-1 text-xs text-gray-400">
                    <Info className="w-3 h-3" />
                    <span>Click a plot to start analysis</span>
                </div>
            </div>

            {/* Chat Panel */}
            <ChatPanel
                gid={selectedGid}
                plotName={plotSummary?.fp_no || (selectedGid ? `Plot ${selectedGid}` : undefined)}
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                onVisualizeTowers={handleVisualizeTowers}
            />
        </div>
    );
};

export default MapV2Page;
